import { Extension } from '@tiptap/react';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import type { Editor, Range } from '@tiptap/react';
import { uploadMedia } from '../../lib/mediaUpload';

export type SlashCommandContext = {
  onCreateSubpage?: (title: string) => Promise<{ id: string; title: string } | null>;
};

export type SlashCommandItem = {
  id: string;
  title: string;
  hint?: string;
  run: (ctx: { editor: Editor; range: Range }) => void | Promise<void>;
};

export function buildSlashItems(ctx: SlashCommandContext): SlashCommandItem[] {
  const items: SlashCommandItem[] = [
    {
      id: 'h1',
      title: 'Heading 1',
      hint: 'Large section title',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run(),
    },
    {
      id: 'h2',
      title: 'Heading 2',
      hint: 'Subsection title',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
    },
    {
      id: 'h3',
      title: 'Heading 3',
      hint: 'Inline title',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
    },
    {
      id: 'bullet',
      title: 'Bullet list',
      hint: 'Unordered list',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBulletList().run(),
    },
    {
      id: 'ordered',
      title: 'Numbered list',
      hint: 'Ordered list',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
    },
    {
      id: 'quote',
      title: 'Quote',
      hint: 'Pull quote with accent bar',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setBlockquote().run(),
    },
    {
      id: 'code',
      title: 'Code block',
      hint: 'Monospaced snippet',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setCodeBlock().run(),
    },
    {
      id: 'callout',
      title: 'Callout',
      hint: 'Highlighted block with emoji',
      run: ({ editor, range }) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent({ type: 'callout', attrs: { emoji: '💡' }, content: [{ type: 'paragraph' }] })
          .run(),
    },
    {
      id: 'divider',
      title: 'Divider',
      hint: 'Horizontal rule',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
    {
      id: 'math-inline',
      title: 'Inline math',
      hint: 'LaTeX rendered inline · $a^2+b^2$',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setInlineMath().run(),
    },
    {
      id: 'math-block',
      title: 'Math block',
      hint: 'LaTeX display equation · $$…$$',
      run: ({ editor, range }) => editor.chain().focus().deleteRange(range).setBlockMath().run(),
    },
    {
      id: 'image',
      title: 'Image',
      hint: 'Upload an image from your computer',
      run: ({ editor, range }) => pickAndInsert(editor, range, 'image/*', 'image'),
    },
    {
      id: 'audio',
      title: 'Audio',
      hint: 'Upload an audio clip',
      run: ({ editor, range }) => pickAndInsert(editor, range, 'audio/*', 'audio'),
    },
    {
      id: 'pdf',
      title: 'PDF',
      hint: 'Embed a PDF document',
      run: ({ editor, range }) => pickAndInsert(editor, range, 'application/pdf', 'pdf'),
    },
    {
      id: 'embed',
      title: 'Embed',
      hint: 'YouTube, Figma, Loom, Spotify, CodePen, Vimeo, Twitch…',
      run: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();
        const url = window.prompt('Paste a URL to embed');
        if (!url) return;
        editor.chain().focus().insertEmbed(url.trim()).run();
      },
    },
  ];

  if (ctx.onCreateSubpage) {
    items.splice(3, 0, {
      id: 'page',
      title: 'Page',
      hint: 'New subpage inside this one',
      run: async ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();
        const created = await ctx.onCreateSubpage!('Untitled');
        if (!created) return;
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'pageLink',
            attrs: { pageId: created.id, title: created.title },
          })
          .insertContent(' ')
          .run();
      },
    });
  }

  return items;
}

// Opens the native file picker, uploads the selected file, and drops in a
// media node at the slash range. If the user cancels the picker we just
// clear the slash input and leave the cursor in place.
function pickAndInsert(
  editor: Editor,
  range: Range,
  accept: string,
  kind: 'image' | 'audio' | 'pdf',
): void {
  editor.chain().focus().deleteRange(range).run();
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const res = await uploadMedia(file);
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'media',
          attrs: {
            mediaId: res.id,
            kind,
            alt: file.name,
            contentType: res.contentType,
            name: res.originalName,
          },
        })
        .run();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Media upload failed', err);
    }
  };
  input.click();
}

type SuggestionRender = Required<SuggestionOptions<SlashCommandItem>>['render'];

export type SlashRenderHandler = ReturnType<SuggestionRender>;

export function createSlashCommand(render: SuggestionRender, context: SlashCommandContext) {
  const items = buildSlashItems(context);
  return Extension.create({
    name: 'slashCommand',
    addOptions() {
      return {
        suggestion: {
          char: '/',
          startOfLine: false,
          command: ({
            editor,
            range,
            props,
          }: {
            editor: Editor;
            range: Range;
            props: SlashCommandItem;
          }) => {
            void props.run({ editor, range });
          },
          items: ({ query }: { query: string }) => {
            const q = query.toLowerCase();
            return items.filter((i) => i.title.toLowerCase().includes(q));
          },
          render,
        },
      };
    },
    addProseMirrorPlugins() {
      return [Suggestion({ editor: this.editor, ...this.options.suggestion })];
    },
  });
}
